import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip
} from "@mui/material";

export default function PracticalGuide() {
  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'primary.main', color: 'white' }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Preparatory Activities for Module 2: Practical Guide on Meal Planning
        </Typography>
      </Paper>

      {/* Week 1 Introduction */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" component="h2" gutterBottom color="primary">
            Week 1 Question: What percentage of your income goes to food?
          </Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            Do not be in a hurry to provide answer to this simple but ambiguous question. You currently do not know the answer.
          </Alert>
          <Typography variant="h6" gutterBottom sx={{ bgcolor: 'yellow', p: 1, borderRadius: 1 }}>
            First step to healthier food consumption pattern starts by knowing your food expenditure:
          </Typography>
        </CardContent>
      </Card>

      {/* Week 1 Activity */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary">
          Week 1 Activity: Daily Food Record Keeping
        </Typography>
        <Typography paragraph>
          Let us start from the very beginning! Very easy and interesting activity.
        </Typography>
        <Typography variant="h6" gutterBottom>
          Start to keep daily record of food intake within and outside the home.
        </Typography>
        
        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
          Tips: Get a 20 leaves exercise book, keep record of:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Daily food and drink consumed within and outside the home." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Every amount spent on the food or drink." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Instant recording, do not wait to record later, you may forget." />
          </ListItem>
          <ListItem>
            <ListItemText primary="At the end of the week (7 days), calculate the amount you spent on feeding." />
          </ListItem>
          <ListItem>
            <ListItemText primary="This activity will run for the next three weeks for better result." />
          </ListItem>
          <ListItem>
            <ListItemText primary="After three weeks, submit your answer to week 1 question." />
          </ListItem>
          <ListItem>
            <ListItemText primary="You will be amazed." />
          </ListItem>
        </List>
      </Paper>

      {/* Sample Table */}
      <Typography variant="h6" gutterBottom>
        Sample Recording Format:
      </Typography>
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Day</strong></TableCell>
              <TableCell><strong>Food Item</strong></TableCell>
              <TableCell><strong>Homemade</strong></TableCell>
              <TableCell><strong>Outside-the-home</strong></TableCell>
              <TableCell><strong>Amount</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Mon</TableCell>
              <TableCell>Boiled rice with egg source, orange, cucumber</TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>No</TableCell>
              <TableCell>₦1000</TableCell>
              <TableCell>30/01/25</TableCell>
            </TableRow>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Fanta, doughnut, ice cream</TableCell>
              <TableCell>No</TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>₦800</TableCell>
              <TableCell></TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Tues</TableCell>
              <TableCell colSpan={5} sx={{ fontStyle: 'italic', color: 'grey.600' }}>
                Continue recording...
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Obstacles and Solutions */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom color="error">
          Possible Obstacles in Tracking Weekly Food Expenditure and Their Solutions:
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Common Obstacles:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText primary="I make monthly bulk food purchases; how can I track my daily food expenditure?" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="I make weekly bulk food purchases; how can I track my daily food expenditures?" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="I share food with my family; how can I track my daily food expenditures?" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="I share food with friends at the hostel; how can I track my daily food expenditures?" />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Solutions:
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Chip label="1. Monthly Bulk Purchases" color="primary" sx={{ mb: 1, mr: 1 }} />
                  <Typography variant="body2">
                    Monitor other consumptions outside the home. After 7 days, divide monthly bulk purchases by 4 and add to record.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                    WFE = (Monthly bulk purchase / 4) + weekly consumption record
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Chip label="2. Weekly Bulk Purchases" color="secondary" sx={{ mb: 1, mr: 1 }} />
                  <Typography variant="body2">
                    Monitor other consumptions outside the home. Sum total bulk purchase to weekly record.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                    WFE = Weekly bulk purchase + weekly consumption record
                  </Typography>
                </Box>

                <Box>
                  <Chip label="3. Sharing Food" color="success" sx={{ mb: 1, mr: 1 }} />
                  <Typography variant="body2">
                    Track individual consumption. Divide bulk food by number of adults in household.
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                    IWFE = (Bulk expenditure / adult family members) + individual weekly expenditure
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Week 2 Sample Data */}
      <Typography variant="h5" gutterBottom sx={{ bgcolor: 'yellow', p: 2, borderRadius: 1 }}>
        Day 4 - Week 2 Sample Record
      </Typography>
      
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.light', color: 'white' }}>
              <TableCell><strong>Day</strong></TableCell>
              <TableCell><strong>Food Item</strong></TableCell>
              <TableCell><strong>Home Made</strong></TableCell>
              <TableCell><strong>Outside Home</strong></TableCell>
              <TableCell><strong>Amount (₦)</strong></TableCell>
              <TableCell><strong>Date</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell rowSpan={6}>Monday (day 1)</TableCell>
              <TableCell>Breakfast</TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>No</TableCell>
              <TableCell>-</TableCell>
              <TableCell>31/01/2025</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Sachet water</TableCell>
              <TableCell>No</TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>100</TableCell>
              <TableCell>31/01/2025</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Doughnut & coca cola</TableCell>
              <TableCell>No</TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>650</TableCell>
              <TableCell>"</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Ground nuts</TableCell>
              <TableCell>No</TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>100</TableCell>
              <TableCell>"</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Lunch</TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>No</TableCell>
              <TableCell>-</TableCell>
              <TableCell>"</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Dinner</TableCell>
              <TableCell>Yes</TableCell>
              <TableCell>No</TableCell>
              <TableCell>-</TableCell>
              <TableCell>"</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'success.light' }}>
              <TableCell colSpan={4}><strong>TOTAL</strong></TableCell>
              <TableCell><strong>₦7,350</strong></TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Calculation Example */}
      <Card sx={{ mb: 4, bgcolor: 'info.light' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Sample Calculation:
          </Typography>
          <Typography paragraph>Monthly food expenditure (MFE) = ₦30,000</Typography>
          <Typography paragraph>Estimated weekly food expenditure (EWFE) with the home: ₦30,000/4 = ₦7,500</Typography>
          <Typography paragraph>Estimated WFE outside the home = ₦7,350</Typography>
          <Typography paragraph sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
            My food expenditure for week 1 is ₦7,500 + ₦7,350 = ₦14,850
          </Typography>
          <Typography variant="h6" color="primary">
            Approximately, I spent ₦15,000 on feeding last week.
          </Typography>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      {/* Module 2 Introduction */}
      <Paper elevation={3} sx={{ p: 3, mb: 4, bgcolor: 'secondary.main', color: 'white' }}>
        <Typography variant="h4" component="h2" gutterBottom align="center">
          Welcome to Module 2: Practical Guide on Meal Planning
        </Typography>
      </Paper>

      <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.6 }}>
        Meal planning is a constructive way of organizing meals for a person or group of people for a specified period of time. 
        It could be targeted on daily, weekly or monthly bases as a person desires. Weekly meal planning is more realizable and 
        cost effective, daily meal planning may be too expensive while monthly meal planning could result to food waste or loss 
        of interest in menu choice. It is a lot easier, cheaper and even healthier to plan meals ahead of time. The benefit of 
        meal planning cannot be overemphasized for an individual, family and households.
      </Typography>

      {/* Objectives */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom color="primary">
          Objectives of Meal Planning:
        </Typography>
        <List>
          <ListItem>
            <ListItemText 
              primary="Encourage intake of home-made meals" 
              secondary="May not be 100% compliance, unless for people that experiences less spending with meal planning and decides to stick to it."
            />
          </ListItem>
          <ListItem>
            <ListItemText 
              primary="Improve cooking skills" 
              secondary="When you prepare your meals regularly, you discover more recipes and varied cooking methods. You learn to cook certain meals differently and not always the usual way."
            />
          </ListItem>
          <ListItem>
            <ListItemText 
              primary="Guide food selection" 
              secondary="Meal planning may reduce monotony of meal consumptions. It will help you identify the food groups that have remained constant in the list."
            />
          </ListItem>
          <ListItem>
            <ListItemText 
              primary="Promote healthy dietary habit" 
              secondary="By preparing and eating more meals at home, you are sure of what you ingest into your body system and could regulate your food intake to a greater extent."
            />
          </ListItem>
        </List>
      </Paper>

      {/* Activity 1 */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h5" gutterBottom color="secondary">
            Activity 1: Practical Guide to Meal Planning
          </Typography>
          <List>
            <ListItem>
              <ListItemText primary="1. How much of home-made meals do you consume in a week? Check your WFE record for the answer (in %)." />
            </ListItem>
            <ListItem>
              <ListItemText primary="2. If your answer to question 1 is above 50%, why do you prefer home-made meals?" />
            </ListItem>
            <ListItem>
              <ListItemText primary="3. If your answer in question 1 is below 50%, why do you prefer eating outside the home?" />
            </ListItem>
            <ListItem>
              <ListItemText primary="4. How do you rate your cooking skill on a scale of 1-5? (1-very adequate; 2-adequate; 3-good; 4-not sure; 5-poor)" />
            </ListItem>
            <ListItem>
              <ListItemText primary="5. From your weekly food expenditure record, how much do you vary your meal intake (in %)?" />
            </ListItem>
            <ListItem>
              <ListItemText primary="6. Which food group dominates your weekly food consumption, and why?" />
            </ListItem>
            <ListItem>
              <ListItemText primary="7. List 20 food materials that are readily available and accessible in your location." />
            </ListItem>
            <ListItem>
              <ListItemText primary="8. Classify your answer in number 7 into: fruits, vegetables, animal sources protein, plant source protein, cereal grains, legumes, nuts & seeds." />
            </ListItem>
          </List>
        </CardContent>
      </Card>

      {/* Sample Meal Plan */}
      <Typography variant="h5" gutterBottom sx={{ bgcolor: 'warning.light', p: 2, borderRadius: 1 }}>
        SAMPLE: ONE WEEK MEAL PLAN
      </Typography>
      
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Day</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Breakfast</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Lunch</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Dinner</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Monday</strong></TableCell>
              <TableCell>
                Oat meal enriched with plant/animal source milk and sugar.<br/>
                2-carrots<br/>
                Drinking water
              </TableCell>
              <TableCell>
                White rice with fish stew and vegetable.<br/>
                Orange whole<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Boiled unripe plantain with crayfish sauce
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Tuesday</strong></TableCell>
              <TableCell>
                Cornflakes/golden morn enriched with plant/animal source milk and sugar.<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Swallow with vegetable soup.<br/>
                Mix fruit puree (pineapple & watermelon)<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Boiled yam with crayfish sauce.<br/>
                Drinking water
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Wednesday</strong></TableCell>
              <TableCell>
                Bread with peanut butter and green/black tea.<br/>
                Banana<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Boiled yam with vegetable (local) sauce.<br/>
                Cucumber and carrot<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Porridge beans.<br/>
                Drinking water
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Thursday</strong></TableCell>
              <TableCell>
                Boiled yam/unripe plantain with crayfish sauce.<br/>
                2 whole oranges<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Swallow with bitter leaf soup<br/>
                Paw-paw/banana<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Boiled rice and beans with beef sauce<br/>
                Drinking water
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Friday</strong></TableCell>
              <TableCell>
                Bread with avocado and soy milk and sugar.<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Unripe plantain portage.<br/>
                Any fruit in season.<br/>
                Drinking water
              </TableCell>
              <TableCell>
                Moi-moi and pap<br/>
                Drinking water
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Alert severity="warning" sx={{ mb: 4 }}>
        <strong>* Use oil, salt and seasonings moderately</strong>
      </Alert>

      {/* Activity 2 */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom color="secondary">
          Activity 2: Weekly Meal Plan
        </Typography>
        
        <Typography variant="h6" gutterBottom>
          i. Using your available weekly food income, prepare a week meal plan:
        </Typography>
        
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Day</strong></TableCell>
                <TableCell><strong>Breakfast</strong></TableCell>
                <TableCell><strong>Lunch</strong></TableCell>
                <TableCell><strong>Dinner</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day) => (
                <TableRow key={day}>
                  <TableCell><strong>{day}</strong></TableCell>
                  <TableCell sx={{ minHeight: 60 }}></TableCell>
                  <TableCell sx={{ minHeight: 60 }}></TableCell>
                  <TableCell sx={{ minHeight: 60 }}></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            ii. Justify your reason for daily choice of meal.
          </Typography>
          <Typography variant="h6" gutterBottom>
            iii. Explain how you intend to purchase the food materials to achieve this week meal plan.
          </Typography>
          <Typography variant="h6" gutterBottom>
            iv. Submit your report on NutriEdu dashboard with your full name and matriculation number.
          </Typography>
        </Box>
      </Paper>

      {/* Contact Information */}
      <Alert severity="info">
        Feel free to contact me for guidance. See you soon!
      </Alert>
    </Box>
  );
}